import formatInTimeZone from 'date-fns-tz/esm/formatInTimeZone';
import utcToZonedTime from 'date-fns-tz/esm/utcToZonedTime';
import zonedTimeToUtc from 'date-fns-tz/esm/zonedTimeToUtc';

import type { Timezone } from './timezones';

export { timezones, type Timezone } from './timezones';

/** ISO 8601 UTC datetime format string matching toISOString() output.
    Always outputs the UTC timezone with the 'Z' suffix, e.g., 2022-07-22T19:15:20.745Z.
    This format ignores the timezone parameter and always formats in UTC.
    It is used for deterministic comparison of dates in a unified format. */
export const ISO_8601_UTC_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";

/**
 * @returns The timestamp in milliseconds since the Unix epoch.
 */
export function getTs(date?: number | string | Date | null): number {
  if (date) {
    return new Date(date).getTime();
  }
  return Date.now();
}

export function userTimeZoneToUtc(
  date: Date | number | string,
  timeZone: Timezone,
): Date {
  // Special case: UTC to UTC is a no-op, return the date as-is
  // zonedTimeToUtc has issues with UTC timezone (adds system timezone offset)
  if (timeZone === 'UTC') {
    return new Date(date);
  }
  return zonedTimeToUtc(date, timeZone);
}

export function utcToUserTimeZone(
  date: Date | number | string,
  timeZone: Timezone,
): Date {
  // Special case: UTC to UTC is a no-op, return the date as-is
  // utcToZonedTime has issues with UTC timezone (subtracts system timezone offset)
  if (timeZone === 'UTC') {
    return new Date(date);
  }
  return utcToZonedTime(date, timeZone);
}

export function getIsoFormattedTs(utcTimestamp?: number): string {
  const date =
    typeof utcTimestamp === 'number' ? new Date(utcTimestamp) : new Date();
  return date.toISOString();
}

export function formatUtcTimestampInTimeZone(
  utcTimestamp: Date | number,
  timeZone: Timezone,
  dateFormat = ISO_8601_UTC_DATETIME_FORMAT,
): string {
  // If using ISO_8601_UTC_DATETIME_FORMAT, always format in UTC regardless of timeZone parameter
  if (dateFormat === ISO_8601_UTC_DATETIME_FORMAT) {
    return formatInTimeZone(utcTimestamp, 'UTC', dateFormat);
  }
  return formatInTimeZone(utcTimestamp, timeZone, dateFormat);
}

export const tableDateFormat = {
  dateFormat: 'yyyy-MM-dd',
  dateTimeFormat: 'yyyy-MM-dd, HH:mm:ss X',
  isoFormat: ISO_8601_UTC_DATETIME_FORMAT,
};

export type TableDateFormat = keyof typeof tableDateFormat;
